import React, { useEffect, useRef, useState } from "react";
import { NavLink, useLocation, useNavigate } from "react-router-dom";
import {
  LayoutDashboard, Users, Trello, ListTodo, UserCog, LogOut, Globe2, Search, Menu,
} from "lucide-react";
import Logo from "./Logo";
import { useAuth } from "../context/AuthContext";
import { useI18n } from "../lib/i18n";
import { Button } from "./ui/button";
import { Input } from "./ui/input";
import { Sheet, SheetContent, SheetTrigger, SheetTitle, SheetDescription } from "./ui/sheet";
import api from "../lib/api";

function GlobalSearch({ compact = false }) {
  const { t } = useI18n();
  const [q, setQ] = useState("");
  const [results, setResults] = useState([]);
  const [open, setOpen] = useState(false);
  const [active, setActive] = useState(0);
  const nav = useNavigate();
  const boxRef = useRef(null);

  useEffect(() => {
    if (q.trim().length < 1) { setResults([]); return; }
    const id = setTimeout(() => {
      api.get("/students", { params: { q, limit: 8 } })
         .then((r) => { setResults(r.data); setActive(0); })
         .catch(() => setResults([]));
    }, 180);
    return () => clearTimeout(id);
  }, [q]);

  useEffect(() => {
    const onClick = (e) => {
      if (boxRef.current && !boxRef.current.contains(e.target)) setOpen(false);
    };
    document.addEventListener("mousedown", onClick);
    return () => document.removeEventListener("mousedown", onClick);
  }, []);

  const go = (id) => {
    nav(`/students/${id}`);
    setOpen(false);
    setQ("");
  };

  const onKey = (e) => {
    if (!open || results.length === 0) return;
    if (e.key === "ArrowDown") { e.preventDefault(); setActive((a) => Math.min(a + 1, results.length - 1)); }
    if (e.key === "ArrowUp") { e.preventDefault(); setActive((a) => Math.max(a - 1, 0)); }
    if (e.key === "Enter") { e.preventDefault(); go(results[active].id); }
    if (e.key === "Escape") setOpen(false);
  };

  return (
    <div ref={boxRef} className="relative w-full max-w-xl" data-testid="global-search">
      <Search size={16} className="absolute inset-y-0 my-auto ms-3 text-slate-400 pointer-events-none" />
      <Input
        placeholder={compact ? t("search") : t("globalSearch")}
        value={q}
        onFocus={() => setOpen(true)}
        onChange={(e) => { setQ(e.target.value); setOpen(true); }}
        onKeyDown={onKey}
        className="ps-9 h-10 bg-white border-slate-200"
        data-testid="global-search-input"
      />
      {open && q && (
        <div className="absolute top-full mt-2 w-full bg-white border border-slate-200 rounded-md shadow-lg z-50 overflow-hidden"
             data-testid="global-search-results">
          {results.length === 0 && (
            <div className="p-4 text-sm text-slate-500">{t("noResults")}</div>
          )}
          {results.map((s, i) => (
            <button key={s.id}
                    onMouseEnter={() => setActive(i)}
                    onClick={() => go(s.id)}
                    className={`w-full text-start px-4 py-2.5 flex items-center justify-between gap-3 border-b border-slate-100 last:border-b-0 ${
                      active === i ? "bg-slate-50" : "hover:bg-slate-50"
                    }`}
                    data-testid={`global-search-result-${s.id}`}>
              <div className="min-w-0">
                <div className="font-medium text-sm text-[#0B192C] truncate">{s.name}</div>
                <div className="text-xs text-slate-500 truncate">
                  {s.phone || s.email || "—"}
                  {s.passport_number ? ` · ${s.passport_number}` : ""}
                </div>
              </div>
              <div className="text-[10px] uppercase tracking-widest text-[#7A1B28] shrink-0">{s.stage}</div>
            </button>
          ))}
        </div>
      )}
    </div>
  );
}

function SidebarContent({ links, user, lang, setLang, t, doLogout, onNavigate }) {
  return (
    <>
      <div className="px-5 py-5 border-b border-slate-200">
        <Logo withWordmark />
      </div>

      <nav className="flex-1 px-3 py-4 space-y-1 overflow-y-auto" data-testid="sidebar-nav">
        <div className="eyebrow px-3 pb-2">{t("appName")}</div>
        {links.map((l) => (
          <NavLink
            key={l.to}
            to={l.to}
            end={l.to === "/"}
            data-testid={l.tid}
            onClick={onNavigate}
            className={({ isActive }) => `sidebar-link ${isActive ? "active" : ""}`}
          >
            <l.icon size={18} strokeWidth={1.6} />
            <span>{l.label}</span>
          </NavLink>
        ))}
      </nav>

      <div className="px-3 py-3 border-t border-slate-200 space-y-2">
        <div className="flex items-center gap-2 px-3 py-2">
          <Globe2 size={16} strokeWidth={1.6} className="text-slate-500" />
          <select
            value={lang}
            onChange={(e) => setLang(e.target.value)}
            className="text-sm bg-transparent border-0 outline-none cursor-pointer"
            data-testid="language-select"
          >
            <option value="en">English</option>
            <option value="ar">العربية</option>
          </select>
        </div>
        {user && (
          <div className="px-3 py-2 rounded-md bg-slate-50 border border-slate-200">
            <div className="text-sm font-medium text-slate-900" data-testid="current-user-name">
              {user.name}
            </div>
            <div className="text-xs text-slate-500 truncate">{user.email}</div>
            <div className="text-[10px] uppercase tracking-widest text-[#7A1B28] mt-1">
              {user.role === "admin" ? t("admin") : t("employee")}
            </div>
          </div>
        )}
        <Button variant="outline"
                className="w-full justify-start gap-2 border-slate-200"
                onClick={doLogout}
                data-testid="logout-btn">
          <LogOut size={16} strokeWidth={1.6} />
          {t("logout")}
        </Button>
      </div>
    </>
  );
}

export default function Layout({ children }) {
  const { user, logout } = useAuth();
  const { t, lang, setLang } = useI18n();
  const nav = useNavigate();
  const location = useLocation();
  const [mobileOpen, setMobileOpen] = useState(false);

  // close drawer on route change
  useEffect(() => { setMobileOpen(false); }, [location.pathname]);

  const links = [
    { to: "/", label: t("dashboard"), icon: LayoutDashboard, tid: "nav-dashboard" },
    { to: "/students", label: t("students"), icon: Users, tid: "nav-students" },
    { to: "/pipeline", label: t("pipeline"), icon: Trello, tid: "nav-pipeline" },
    { to: "/tasks", label: t("tasks"), icon: ListTodo, tid: "nav-tasks" },
  ];
  if (user && user.role === "admin") {
    links.push({ to: "/team", label: t("team"), icon: UserCog, tid: "nav-team" });
  }

  const doLogout = async () => {
    await logout();
    nav("/login");
  };

  const sidebarSide = lang === "ar" ? "right" : "left";

  return (
    <div className="min-h-screen flex bg-[#FDFBF7]">
      {/* Desktop sidebar */}
      <aside className="hidden lg:flex w-64 shrink-0 bg-white border-e border-slate-200 flex-col"
             data-testid="app-sidebar">
        <SidebarContent
          links={links} user={user} lang={lang} setLang={setLang}
          t={t} doLogout={doLogout}
        />
      </aside>

      <div className="flex-1 min-w-0 flex flex-col" data-testid="main-content">
        {/* Top bar: mobile shows hamburger + logo; desktop shows search only */}
        <header className="h-16 bg-white border-b border-slate-200 flex items-center px-4 sm:px-6 gap-3"
                data-testid="app-topbar">
          <Sheet open={mobileOpen} onOpenChange={setMobileOpen}>
            <SheetTrigger asChild>
              <Button variant="outline" size="icon"
                      className="lg:hidden border-slate-200"
                      data-testid="mobile-menu-btn"
                      aria-label="Open menu">
                <Menu size={18} strokeWidth={1.6} />
              </Button>
            </SheetTrigger>
            <SheetContent side={sidebarSide}
                          className="p-0 w-72 max-w-[85vw] flex flex-col"
                          data-testid="mobile-sidebar">
              <SidebarContent
                links={links} user={user} lang={lang} setLang={setLang}
                t={t} doLogout={doLogout}
                onNavigate={() => setMobileOpen(false)}
              />
            </SheetContent>
          </Sheet>

          <div className="lg:hidden flex-shrink-0">
            <Logo size={32} />
          </div>

          <div className="flex-1 min-w-0">
            <GlobalSearch />
          </div>
        </header>

        <main className="flex-1 min-h-0 min-w-0">{children}</main>
      </div>
    </div>
  );
}
